import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface ThemeColors {
  accent: string;
  accentHover: string;
  accentSubtle: string;
  name: string;
}

export interface BgMode {
  name: string;
  icon: string;
  bg: string;
  bgRaised: string;
  bgSurface: string;
  bgHover: string;
  bgGlass: string;
  cardBg: string;
  cardBorder: string;
  cardShadow: string;
  text: string;
  textSecondary: string;
  textMuted: string;
  border: string;
  borderSubtle: string;
  tableHeaderBg: string;
  inputBg: string;
  scrollThumb: string;
  scrollHover: string;
  selectionBg: string;
  bodyBgImage: string;
  chartGrid: string;
  tooltipBg: string;
  tooltipBorder: string;
  badgeMuted: string;
}

const THEMES: Record<string, ThemeColors> = {
  indigo: { accent: '#6366f1', accentHover: '#818cf8', accentSubtle: 'rgba(99,102,241,0.08)', name: 'Indigo' },
  blue: { accent: '#3b82f6', accentHover: '#60a5fa', accentSubtle: 'rgba(59,130,246,0.08)', name: 'Blue' },
  violet: { accent: '#8b5cf6', accentHover: '#a78bfa', accentSubtle: 'rgba(139,92,246,0.08)', name: 'Violet' },
  emerald: { accent: '#10b981', accentHover: '#34d399', accentSubtle: 'rgba(16,185,129,0.08)', name: 'Emerald' },
  rose: { accent: '#f43f5e', accentHover: '#fb7185', accentSubtle: 'rgba(244,63,94,0.08)', name: 'Rose' },
  amber: { accent: '#f59e0b', accentHover: '#fbbf24', accentSubtle: 'rgba(245,158,11,0.08)', name: 'Amber' },
  cyan: { accent: '#06b6d4', accentHover: '#22d3ee', accentSubtle: 'rgba(6,182,212,0.08)', name: 'Cyan' },
  orange: { accent: '#f97316', accentHover: '#fb923c', accentSubtle: 'rgba(249,115,22,0.08)', name: 'Orange' },
};

const BG_MODES: Record<string, BgMode> = {
  light: {
    name: 'Light',
    icon: '☀️',
    bg: '#ffffff',
    bgRaised: '#f8f9fb',
    bgSurface: '#f1f3f7',
    bgHover: '#ebedf2',
    bgGlass: 'rgba(255,255,255,0.85)',
    cardBg: '#ffffff',
    cardBorder: '#e8eaef',
    cardShadow: '0 1px 3px rgba(0,0,0,0.04)',
    text: '#111827',
    textSecondary: '#4b5563',
    textMuted: '#9ca3af',
    border: '#e2e5eb',
    borderSubtle: '#eef0f4',
    tableHeaderBg: '#fafbfc',
    inputBg: '#f8f9fb',
    scrollThumb: '#d0d3dc',
    scrollHover: '#b4b8c4',
    selectionBg: 'rgba(99,102,241,0.15)',
    bodyBgImage: 'radial-gradient(ellipse 90% 50% at 0% 0%, rgba(99,102,241,0.04) 0%, transparent 60%), radial-gradient(ellipse 60% 40% at 100% 100%, rgba(168,85,247,0.03) 0%, transparent 50%)',
    chartGrid: '#f3f4f6',
    tooltipBg: '#ffffff',
    tooltipBorder: '#e5e7eb',
    badgeMuted: '#f3f4f6',
  },
  dark: {
    name: 'Dark',
    icon: '🌙',
    bg: '#111318',
    bgRaised: '#1a1d25',
    bgSurface: '#22252f',
    bgHover: '#2a2e3a',
    bgGlass: 'rgba(17,19,24,0.90)',
    cardBg: '#1a1d25',
    cardBorder: '#2a2e3a',
    cardShadow: '0 1px 3px rgba(0,0,0,0.3)',
    text: '#f1f2f6',
    textSecondary: '#a0a4b8',
    textMuted: '#6b7085',
    border: '#2a2e3a',
    borderSubtle: '#22252f',
    tableHeaderBg: '#1e2129',
    inputBg: '#22252f',
    scrollThumb: '#3a3e4a',
    scrollHover: '#4a4e5a',
    selectionBg: 'rgba(99,102,241,0.25)',
    bodyBgImage: 'radial-gradient(ellipse 90% 50% at 0% 0%, rgba(99,102,241,0.06) 0%, transparent 60%), radial-gradient(ellipse 60% 40% at 100% 100%, rgba(168,85,247,0.04) 0%, transparent 50%)',
    chartGrid: '#2a2e3a',
    tooltipBg: '#1e2129',
    tooltipBorder: '#2a2e3a',
    badgeMuted: '#22252f',
  },
  midnight: {
    name: 'Midnight',
    icon: '🌌',
    bg: '#0a0e1a',
    bgRaised: '#111628',
    bgSurface: '#1a2038',
    bgHover: '#222845',
    bgGlass: 'rgba(10,14,26,0.92)',
    cardBg: '#111628',
    cardBorder: '#1e2845',
    cardShadow: '0 1px 4px rgba(0,0,0,0.4)',
    text: '#e8ebf5',
    textSecondary: '#8b92b0',
    textMuted: '#5a6180',
    border: '#1e2845',
    borderSubtle: '#162035',
    tableHeaderBg: '#141a30',
    inputBg: '#1a2038',
    scrollThumb: '#2a3455',
    scrollHover: '#3a4465',
    selectionBg: 'rgba(99,102,241,0.3)',
    bodyBgImage: 'radial-gradient(ellipse 90% 50% at 0% 0%, rgba(99,102,241,0.08) 0%, transparent 60%), radial-gradient(ellipse 60% 40% at 100% 100%, rgba(59,130,246,0.06) 0%, transparent 50%)',
    chartGrid: '#1e2845',
    tooltipBg: '#141a30',
    tooltipBorder: '#1e2845',
    badgeMuted: '#1a2038',
  },
  slate: {
    name: 'Slate',
    icon: '🪨',
    bg: '#f0f2f5',
    bgRaised: '#e8eaef',
    bgSurface: '#dde0e7',
    bgHover: '#d2d5de',
    bgGlass: 'rgba(240,242,245,0.88)',
    cardBg: '#ffffff',
    cardBorder: '#d1d5db',
    cardShadow: '0 1px 3px rgba(0,0,0,0.06)',
    text: '#1e293b',
    textSecondary: '#475569',
    textMuted: '#94a3b8',
    border: '#cbd5e1',
    borderSubtle: '#e2e8f0',
    tableHeaderBg: '#f1f5f9',
    inputBg: '#f8fafc',
    scrollThumb: '#b8c0cc',
    scrollHover: '#a0a8b8',
    selectionBg: 'rgba(99,102,241,0.15)',
    bodyBgImage: 'radial-gradient(ellipse 80% 50% at 10% 0%, rgba(99,102,241,0.05) 0%, transparent 60%), radial-gradient(ellipse 50% 40% at 90% 100%, rgba(59,130,246,0.04) 0%, transparent 50%)',
    chartGrid: '#e2e8f0',
    tooltipBg: '#ffffff',
    tooltipBorder: '#d1d5db',
    badgeMuted: '#e2e8f0',
  },
  warm: {
    name: 'Warm',
    icon: '🍂',
    bg: '#faf8f5',
    bgRaised: '#f5f0ea',
    bgSurface: '#ede5db',
    bgHover: '#e5dcd0',
    bgGlass: 'rgba(250,248,245,0.88)',
    cardBg: '#fffefa',
    cardBorder: '#e5ddd2',
    cardShadow: '0 1px 3px rgba(120,80,30,0.06)',
    text: '#292524',
    textSecondary: '#57534e',
    textMuted: '#a8a29e',
    border: '#d6d3d1',
    borderSubtle: '#e7e5e4',
    tableHeaderBg: '#f5f2ee',
    inputBg: '#faf8f5',
    scrollThumb: '#c8c2b8',
    scrollHover: '#b0a898',
    selectionBg: 'rgba(245,158,11,0.15)',
    bodyBgImage: 'radial-gradient(ellipse 80% 50% at 0% 0%, rgba(245,158,11,0.05) 0%, transparent 60%), radial-gradient(ellipse 60% 40% at 100% 100%, rgba(234,88,12,0.03) 0%, transparent 50%)',
    chartGrid: '#e7e5e4',
    tooltipBg: '#fffefa',
    tooltipBorder: '#e5ddd2',
    badgeMuted: '#ede5db',
  },
};

interface ThemeContextType {
  theme: ThemeColors;
  themeName: string;
  setTheme: (name: string) => void;
  themes: Record<string, ThemeColors>;
  bgMode: BgMode;
  bgModeName: string;
  setBgMode: (name: string) => void;
  bgModes: Record<string, BgMode>;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

function applyBgMode(mode: BgMode) {
  const root = document.documentElement;
  root.style.setProperty('--bg', mode.bg);
  root.style.setProperty('--bg-raised', mode.bgRaised);
  root.style.setProperty('--bg-surface', mode.bgSurface);
  root.style.setProperty('--bg-hover', mode.bgHover);
  root.style.setProperty('--bg-glass', mode.bgGlass);
  root.style.setProperty('--card-bg', mode.cardBg);
  root.style.setProperty('--card-border', mode.cardBorder);
  root.style.setProperty('--card-shadow', mode.cardShadow);
  root.style.setProperty('--text', mode.text);
  root.style.setProperty('--text-secondary', mode.textSecondary);
  root.style.setProperty('--text-muted', mode.textMuted);
  root.style.setProperty('--border', mode.border);
  root.style.setProperty('--border-subtle', mode.borderSubtle);
  root.style.setProperty('--table-header-bg', mode.tableHeaderBg);
  root.style.setProperty('--input-bg', mode.inputBg);
  root.style.setProperty('--chart-grid', mode.chartGrid);
  root.style.setProperty('--tooltip-bg', mode.tooltipBg);
  root.style.setProperty('--tooltip-border', mode.tooltipBorder);
  root.style.setProperty('--badge-muted', mode.badgeMuted);
  // Keep RGB triple in sync so `rgba(var(--card-bg-rgb), 0.x)` consumers
  // get the right base color when the user switches themes.
  const cardRgb = hexToRgbTriple(mode.cardBg) || '255, 255, 255';
  root.style.setProperty('--card-bg-rgb', cardRgb);
  document.body.style.background = mode.bg;
  document.body.style.backgroundImage = mode.bodyBgImage;
  document.body.style.backgroundAttachment = 'fixed';
}

// Hex (#rrggbb / #rgb) → "r, g, b" string. Returns null for non-hex (rgba, etc.).
function hexToRgbTriple(hex: string): string | null {
  if (!hex || typeof hex !== 'string') return null;
  let m = hex.trim();
  if (m.startsWith('#')) m = m.slice(1);
  if (m.length === 3) m = m.split('').map(c => c + c).join('');
  if (m.length !== 6 || /[^0-9a-fA-F]/.test(m)) return null;
  const r = parseInt(m.slice(0, 2), 16);
  const g = parseInt(m.slice(2, 4), 16);
  const b = parseInt(m.slice(4, 6), 16);
  return `${r}, ${g}, ${b}`;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [themeName, setThemeName] = useState(() => localStorage.getItem('hala-theme') || 'indigo');
  const [bgModeName, setBgModeName] = useState(() => localStorage.getItem('hala-bg-mode') || 'light');

  const theme = THEMES[themeName] || THEMES.indigo;
  const bgMode = BG_MODES[bgModeName] || BG_MODES.light;

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--accent', theme.accent);
    root.style.setProperty('--accent-hover', theme.accentHover);
    root.style.setProperty('--accent-subtle', theme.accentSubtle);
    localStorage.setItem('hala-theme', themeName);
  }, [theme, themeName]);

  useEffect(() => {
    applyBgMode(bgMode);
    localStorage.setItem('hala-bg-mode', bgModeName);
  }, [bgMode, bgModeName]);

  const setTheme = (name: string) => {
    if (THEMES[name]) setThemeName(name);
  };

  const setBgModeHandler = (name: string) => {
    if (BG_MODES[name]) setBgModeName(name);
  };

  return (
    <ThemeContext.Provider value={{
      theme, themeName, setTheme, themes: THEMES,
      bgMode, bgModeName, setBgMode: setBgModeHandler, bgModes: BG_MODES,
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
